import BaseAppService from "../../../../application/platform/official-daily/basa-app-service";
import AuthenticatorAppService from "../../../../application/platform/security/authenticator-app-service";
import { ACTION_TYPES, useClientsManagementDispatch, useClientsManagementState } from "./clients-management-context";
import { IListClientsRequest } from "../../../../model/base/request/list-clients-request";
import { ICreateClientRequest } from "../../../../model/base/request/create-client-request";
import { IUpdateClientRequest } from "../../../../model/base/request/update-client-request";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";

export const useClientsManagementController = () => {
    const { clientsPagedRequest, clientForm, isEditing, brandLogo } = useClientsManagementState();
    const clientsManagementDispatch = useClientsManagementDispatch();
    const { showBackDrop, showMessage } = useBaseLayoutController();


    const loadData = async (request: IListClientsRequest) => {
        if (request?.page !== undefined && request?.pageSize !== undefined) {
            clientsManagementDispatch({
                type: ACTION_TYPES.setIsLoadingData,
                payload: true,
            });

            try {
                const data = await BaseAppService.listClients(request);

                clientsManagementDispatch({
                    type: ACTION_TYPES.setClientsPagedResponse,
                    payload: data,
                });
            } catch (error) {
                console.error('Error loading clients:', error);
            } finally {
                clientsManagementDispatch({
                    type: ACTION_TYPES.setIsLoadingData,
                    payload: false,
                });
            }
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        var request = { ...clientsPagedRequest, page, pageSize } as IListClientsRequest
        clientsManagementDispatch({
            type: ACTION_TYPES.setClientsPagedRequest,
            payload: request,
        });

        await loadData(request);
    };

    const handleFilterChange = (e: any) => {
        let name = clientsPagedRequest?.name;
        let document = clientsPagedRequest?.document;
        let entityType = clientsPagedRequest?.entityType;

        if (e.target.name === "input-name") {
            name = e.target.value;
        }
        if (e.target.name === "input-document") {
            document = e.target.value;
        }
        if (e.target.name === "select-entity-type") {
            entityType = e.target.value ? parseInt(e.target.value) : undefined;
        }

        clientsManagementDispatch({
            type: ACTION_TYPES.setClientsPagedRequest,
            payload: { ...clientsPagedRequest, name, document, entityType },
        });
    };

    const clearFilters = async () => {
        const request = {
            page: 0,
            pageSize: 50,
            name: "",
            document: "",
            entityType: undefined
        } as IListClientsRequest;

        clientsManagementDispatch({
            type: ACTION_TYPES.setClientsPagedRequest,
            payload: request,
        });

        await loadData(request);
    };

    const searchClients = async () => {
        const request = { ...clientsPagedRequest, page: 0 };
        clientsManagementDispatch({
            type: ACTION_TYPES.setClientsPagedRequest,
            payload: request,
        });
        await loadData(request);
    };

    const loadClientById = async (id: number) => {
        showBackDrop(true);
        clientsManagementDispatch({
            type: ACTION_TYPES.setIsLoadingClient,
            payload: true,
        });

        try {
            const client = await BaseAppService.getClientById(id);
            console.log('Loaded client:', client);
            clientsManagementDispatch({
                type: ACTION_TYPES.setSelectedClient,
                payload: client,
            });

            const formData = {
                id: client.id,
                name: client.name,
                corporateName: client.corporateName,
                document: client.document,
                brandLogo: client.brandLogo,
                entityType: client.entityType,
                webPage: client.webPage,
                instagramProfile: client.instagramProfile,
                facebookProfile: client.facebookProfile,
                twitterProfile: client.twitterProfile,
                youtubeProfile: client.youtubeProfile,
                desUrlTransparencia: client.desUrlTransparencia,
                desUrlServidores: client.desUrlServidores,
                desUrlOuvidoria: client.desUrlOuvidoria,
                desUrlLicitacoes: client.desUrlLicitacoes,
                desUrlDiarioOficial: client.desUrlDiarioOficial,
                desUrlSic: client.desUrlSic,
                address: client.address ? {
                    street: client.address.street,
                    number: client.address.number,
                    complement: client.address.complement,
                    neighborhood: client.address.neighborhood,
                    postalCode: client.address.zipCode,
                    stateId: client.address.stateId,
                    cityId: client.address.cityId
                } : undefined,
                contact: client.contact ? {
                    email: client.contact.email,
                    phone: client.contact.mobilePhone,
                    website: ""
                } : undefined
            } as ICreateClientRequest;

            clientsManagementDispatch({
                type: ACTION_TYPES.setClientForm,
                payload: formData,
            });
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao carregar cliente.' });
        } finally {
            showBackDrop(false);

            clientsManagementDispatch({
                type: ACTION_TYPES.setIsLoadingClient,
                payload: false,
            });
        }
    };

    const handleFormChange = (field: string, value: any) => {
        clientsManagementDispatch({
            type: ACTION_TYPES.setClientForm,
            payload: { ...clientForm, [field]: value },
        });
    };

    const handleAddressChange = (field: string, value: any) => {
        const currentAddress = clientForm?.address || { cityId: 0 };
        const updatedForm = {
            ...clientForm,
            address: { ...currentAddress, [field]: value }
        };

        clientsManagementDispatch({
            type: ACTION_TYPES.setClientForm,
            payload: updatedForm,
        });
    };

    const updateAddress = (addressUpdates: any) => {
        const currentAddress = clientForm?.address || { cityId: 0 };
        const updatedForm = {
            ...clientForm,
            address: { ...currentAddress, ...addressUpdates }
        };

        clientsManagementDispatch({
            type: ACTION_TYPES.setClientForm,
            payload: updatedForm,
        });
    };

    const handleContactChange = (field: string, value: any) => {
        clientsManagementDispatch({
            type: ACTION_TYPES.setClientForm,
            payload: {
                ...clientForm,
                contact: { ...clientForm.contact, [field]: value }
            },
        });
    };

    const openDrawer = (editMode = false, clientId?: number) => {
        clientsManagementDispatch({
            type: ACTION_TYPES.setIsEditing,
            payload: editMode,
        });

        if (editMode && clientId) {
            loadClientById(clientId);
        } else {
            clientsManagementDispatch({
                type: ACTION_TYPES.resetClientForm,
                payload: null,
            });
        }

        clientsManagementDispatch({
            type: ACTION_TYPES.setIsDrawerOpen,
            payload: true,
        });
    };

    const closeDrawer = () => {
        clientsManagementDispatch({
            type: ACTION_TYPES.setIsDrawerOpen,
            payload: false,
        });

        clientsManagementDispatch({
            type: ACTION_TYPES.setSelectedClient,
            payload: null,
        });

        clientsManagementDispatch({
            type: ACTION_TYPES.setBrandLogo,
            payload: null,
        });

        clientsManagementDispatch({
            type: ACTION_TYPES.resetClientForm,
            payload: null,
        });
    };

    const saveClient = async () => {
        try {

            showBackDrop(true);
            console.log('clientForm:', clientForm);
            if (isEditing && clientForm.id) {
                const updateRequest = { ...clientForm, id: clientForm.id } as IUpdateClientRequest;
                await BaseAppService.updateClient(updateRequest, brandLogo);
            } else {
                await BaseAppService.createClient(clientForm, brandLogo);
            }

            showMessage({ type: 'success', message: 'Cliente salvo com sucesso.' });

            closeDrawer();

            await loadData(clientsPagedRequest);
            showBackDrop(false);

        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao salvar cliente.' });
        }
    };

    const loadStates = async () => {
        try {
            const result = await AuthenticatorAppService.listStates();
            clientsManagementDispatch({
                type: ACTION_TYPES.setListStateResponse,
                payload: result,
            });
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao carregar estados.' });
        }
    };

    const loadCities = async (stateId: number) => {
        try {
            const result = await AuthenticatorAppService.listCities({ stateId });
            clientsManagementDispatch({
                type: ACTION_TYPES.setListCityResponse,
                payload: result,
            });
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao carregar cidades.' });

        }
    };

    return {
        loadData,
        handleFilterChange,
        onChangePagination,
        clearFilters,
        searchClients,
        loadClientById,
        handleFormChange,
        handleAddressChange,
        handleContactChange,
        openDrawer,
        closeDrawer,
        saveClient,
        loadStates,
        loadCities,
        updateAddress,
        setBrandLogo: (file: File) => {
            clientsManagementDispatch({
                type: ACTION_TYPES.setBrandLogo,
                payload: file,
            });
        }
    };
};